/**
 * Rebuilds a model's down axis from a list of rows, for the tile views that
 * add, drop and reorder rows (measures, category display). The header tree
 * is rebuilt from the new leaves and the significance positions that count
 * down rows follow their rows.
 */

import type {
  AxisLeaf,
  CellSig,
  ModelCell,
  SigMeta,
  TableModel,
} from "utils/tableModel"
import { buildHeaderTree } from "utils/tableModel"

/** One row of a derived model; `from` is its index in the source model. */
export interface DownRow {
  leaf: AxisLeaf
  cells: ModelCell[]
  from?: number
}

/** The rows of `model`, each with its index. */
export const downRowsOf = (model: TableModel): DownRow[] =>
  model.down.leaves.map((leaf, i) => ({
    leaf,
    cells: model.cells[i] ?? [],
    from: i,
  }))

/** `cell` with its letters position moved to the row's new index, or dropped. */
const relocate = (cell: ModelCell, moved: Map<number, number>): ModelCell => {
  const at = cell.sig?.lettersAt
  if (!cell.sig || !at) return cell
  const row = moved.get(at.row)
  if (row === at.row) return cell
  const sig: CellSig = { ...cell.sig }
  if (row === undefined) delete sig.lettersAt
  else sig.lettersAt = { row, col: at.col }
  return { ...cell, sig }
}

/**
 * Significance with the element spans moved to the rows' new indices when
 * the banner runs down (an element whose rows are all gone keeps an empty
 * span, so the letters that name it still find it). Undefined when an
 * element's rows are no longer next to each other.
 */
const remapSignificance = (
  meta: SigMeta,
  moved: Map<number, number>
): SigMeta | undefined => {
  if (meta.bannerAxis !== "down") return meta
  let split = false
  const elements = meta.elements.map((element) => {
    const rows: number[] = []
    for (let i = element.span[0]; i < element.span[1]; i++) {
      const to = moved.get(i)
      if (to !== undefined) rows.push(to)
    }
    if (rows.length === 0) return { ...element, span: [0, 0] as [number, number] }
    const first = Math.min(...rows)
    const last = Math.max(...rows)
    if (last - first + 1 !== rows.length) split = true
    return { ...element, span: [first, last + 1] as [number, number] }
  })
  return split ? undefined : { ...meta, elements }
}

/**
 * `model` with `rows` as its down axis, in order: the header tree rebuilt
 * from the leaves (a category's statistic rows stay under one header), the
 * depth kept, each cell's letters position moved with its row (dropped when
 * that row is gone) and, with the banner down, the element spans moved too.
 */
export const withDownRows = (model: TableModel, rows: DownRow[]): TableModel => {
  const moved = new Map<number, number>()
  rows.forEach((row, i) => {
    if (row.from !== undefined) moved.set(row.from, i)
  })
  const leaves = rows.map((row) => row.leaf)
  const view: TableModel = {
    ...model,
    down: {
      depth: model.down.depth,
      leaves,
      tree: buildHeaderTree(leaves),
    },
    cells: rows.map((row) => row.cells.map((cell) => relocate(cell, moved))),
  }
  const significance = model.significance
    ? remapSignificance(model.significance, moved)
    : undefined
  if (significance) view.significance = significance
  else delete view.significance
  return view
}
